import React, { useMemo } from 'react';
import formatDataForSelect from 'Shared/util/formatDataForSelect';
import { useLazyQuery, useQuery } from '@apollo/client';
import * as Yup from 'yup';

import { AdminFormTab } from 'Components/Admin/Form';
import { CompanyCard } from '../../../../components';
import { queryGetBanks, queryGetCompanies, queryGetPaymentTerms } from '../../query';
import CompanySVG from 'Components/Admin/assets/CompanySVG';
import PrimaryContactSVG from 'Components/Admin/assets/PrimaryContactSVG';

const tempOptions = [
  { id: 1, title: 'Title 1' },
  { id: 2, title: 'Title 2' },
];

export const projectStatuses = [
  { id: 'new', title: 'New' },
  { id: 'in-progress', title: 'In progress' },
  { id: 'on-hold', title: 'On hold' },
  { id: 'finished', title: 'Finished' },
];

const GeneralContentForm = () => {
  const { data: paymentTermsData } = useQuery(queryGetPaymentTerms);
  const [fetchCompanies, { data: companiesData }] = useLazyQuery(queryGetCompanies);
  const [fetchBanks, { data: banksData }] = useLazyQuery(queryGetBanks);

  const handleSubmit = (data) => console.log(data);

  const formItems = useMemo(() => (
    [
      {
        name: 'title',
        type: 'text',
        label: 'Title',
        subLabel: `The name of the project that will   be displayed in the table. You can title the project whatever you want,
            the customer will not see it.`,
        placeholder: 'Enter title',
      }, {
      name: 'company',
      type: 'card',
      cardName: 'company',
      placeholder: 'Select company',
      subTitle: `The company that owns the project. If the required company is not in the list, you can add it or edit it.`,
      emptyStateSubTitle: `The company that owns the project. Choose a company from the list.
              If this does not suit you, you can change or add a new one.`,
      changeModeDescription: `In the list you will find all companies that are in the system. You can choose from existing 
              ones or create a new one.`,
      entities: companiesData?.companies,
      fetchEntities: fetchCompanies,
      getTitle: (company) => company?.title || 'Owner company',
      Body: CompanyCard,
      subForm: {
        pages: [
          {
            items: [
              {
                type: 'text',
                name: 'title',
                placeholder: 'Type company title',
                label: 'Title',
                validation: Yup.string()
                  .required('This field is required'),
              },
              {
                type: 'select',
                name: 'country',
                placeholder: 'Select country',
                label: 'Country',
                options: formatDataForSelect(tempOptions),
              },
              {
                type: 'select',
                name: 'city',
                placeholder: 'Select city',
                label: 'City',
                options: formatDataForSelect(tempOptions),
              },
              {
                type: 'text',
                name: 'address1',
                placeholder: 'Type company address',
                label: 'Address',
              },
              [
                {
                  type: 'text',
                  name: 'zip',
                  placeholder: 'Type company zip',
                  label: 'Zip',
                  rowClassName: 'general-content-form__row_zip',
                },
                {
                  type: 'text',
                  name: 'vatNumber',
                  placeholder: 'Type company vat number',
                  label: 'Vat number',
                },
              ],
            ],
          }, {
            items: [
              {
                type: 'text',
                name: 'email',
                placeholder: 'Type company email',
                label: 'Email',
              },
              {
                type: 'text',
                name: 'website',
                placeholder: 'Type company website',
                label: 'Website',
              },
              {
                type: 'text',
                name: 'linkedIn',
                placeholder: 'Type company LinkedIn',
                label: 'LinkedIn',
              },
              {
                type: 'select',
                name: 'industry',
                placeholder: 'Select industries',
                label: 'Industry',
                multiple: true,
                options: formatDataForSelect(tempOptions),
              },
              {
                type: 'textarea',
                name: 'note',
                placeholder: 'Type some note...',
                label: 'Note',
              },
            ],
          },
        ],
        onSubmit: handleSubmit,
        pathName: 'company',
        initialValues: {
          title: '',
          country: null,
          city: null,
          address1: '',
          zip: '',
          vatNumber: '',
          website: '',
          note: 'asdsa',
        },
      },
    }, {
      name: 'defaultBank',
      type: 'select',
      label: 'Default bank',
      subLabel: `The bank that will be displayed in invoices and orders. If this does not suit you, you can change or add a new one.`,
      placeholder: 'Select default bank',
      options: formatDataForSelect(banksData?.banks || []),
      fetchOptions: fetchBanks,
      subForm: {
        items: [
          {
            type: 'text',
            placeholder: 'Enter bank name',
            name: 'name',
          },
        ],
        pathName: 'default-bank',
        onSubmit: handleSubmit,
      },
    }, {
      name: 'reference',
      type: 'text',
      label: 'Reference',
      subLabel: `This project ID will appear in the project list. You can come up with this yourself or it will be generated automatically.`,
      placeholder: 'Enter reference',
      validation: Yup.string()
        .required('This field is required'),
    }, {
      type: 'radio',
      name: 'defaultTerms',
      label: 'Default payment terms',
      subLabel: 'Duration of the project. If these terms do not fit you can change them.',
      options: formatDataForSelect(paymentTermsData?.paymentTerms || []),
      subForm: {
        items: [
          {
            type: 'text',
            name: 'title',
            label: 'Title',
            placeholder: 'Type term title',
          },
        ],
        pathName: 'default-terms',
        onSubmit: handleSubmit,
      },
    },
      [
        {
          name: 'startDate',
          type: 'date',
          label: 'Project start date',
          subLabel: 'Actual start date of project development.',
          placeholder: 'Choose start date',
        },
        {
          name: 'endDate',
          type: 'date',
          label: 'Project end date',
          subLabel: 'Actual start date of project development.',
          placeholder: 'Choose end date',
        },
      ],
      {
        type: 'radio',
        name: 'state',
        label: 'State',
        subLabel: 'Select the status of the project development. This will be displayed in the corresponding tab in the list of projects.',
        options: formatDataForSelect(projectStatuses),
      },
      {
        type: 'tabs',
        name: 'exposingBill',
        label: 'Exposing bill',
        subLabel: 'Select who should appear in the document as "Customer".',
        variant: 'primary',
        options: [
          {
            id: 1,
            title: 'Owner company',
            description: 'We will bill the company that owns the project.',
            footer: 'Spears LLC',
            getIcon: (isActive) => <CompanySVG isActive={isActive} />,
          },
          {
            id: 2,
            title: 'Primary contact',
            description: 'We will bill the primary contact from contact list.',
            footer: 'Mariana Dickey',
            getIcon: (isActive) => <PrimaryContactSVG isActive={isActive} />,
          },
        ],
      },
    ]
  ), [companiesData, paymentTermsData, banksData]);

  return (
    <div className="general-content-form" data-testid="projects-general-form">
      <AdminFormTab formItems={formItems} />
    </div>
  );
};

export default GeneralContentForm;
